import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOperatorAuth } from '../context/OperatorAuthContext';
import { ApiError } from '../api/client';
import { useFragmentToken } from '../hooks/useFragmentToken';

/** The server's floor (operatorPasswordSchema): 12..128, plus a weak-pattern check only it knows. */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Landing page for the reset mail: /reset-password#token=…
 * Public: an operator who has lost their password has no session.
 */
const ResetPasswordPage: React.FC = () => {
  const { api } = useOperatorAuth();
  const navigate = useNavigate();
  const token = useFragmentToken();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A spent or expired token cannot be retried; a rejected password can,
  // because the server validates the password before it spends the token.
  const [tokenDead, setTokenDead] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.resetPassword(token, password);
      navigate('/login', { replace: true, state: { notice: res.message } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.code === 'INVALID_TOKEN') setTokenDead(true);
      } else {
        setError('Password reset failed.');
      }
      setSubmitting(false);
    }
  };

  if (!token || tokenDead) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <h1>Reset password</h1>
          <div className="auth-error" role="alert">
            {error ?? 'This page needs the link from the reset email. Open that link again, or ask for a new one.'}
          </div>
          <p className="auth-foot">
            <Link to="/login" state={{ forgot: true }}>Ask for a new reset link</Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <h1>Reset password</h1>
        <p className="auth-sub">
          Choose a new password. Every session on the account will be signed out.
        </p>
        <form onSubmit={onSubmit} className="auth-form">
          <label>
            <span>New password</span>
            <input
              type="password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={128}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </label>
          <label>
            <span>Confirm new password</span>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </label>
          <p className="auth-hint">At least {MIN_PASSWORD_LENGTH} characters.</p>
          {error && <div className="auth-error" role="alert">{error}</div>}
          <button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Set new password'}
          </button>
        </form>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
